import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { apiFetch, useBackendAuth } from '../services/apiClient';

export type AuthUser = { id: string; email: string; role: 'admin' | 'user' };

type AuthContextValue = {
  enabled: boolean;
  user: AuthUser | null;
  isAdmin: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const enabled = useBackendAuth;
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(enabled);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      const data = await apiFetch<{ user: AuthUser }>('/api/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), password }),
    });
    flushSync(() => setUser(data.user));
    return data.user;
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ user: AuthUser }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), password }),
    });
    flushSync(() => setUser(data.user));
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    if (enabled) {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    }
    setUser(null);
  }, [enabled]);

  const isAdmin = user?.role === 'admin';

  const value = useMemo(
    () => ({ enabled, user, isAdmin, loading, login, register, logout, refresh }),
    [enabled, user, isAdmin, loading, login, register, logout, refresh]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return v;
}
